import { ArrowRight, Bike, CheckCircle2, EyeOff, HandPlatter, Store, X, XCircle } from "lucide-react";
import { useState } from "react";

import ItemLinha, { Observacao } from "./ItemLinha";
import Timer from "./Timer";
import { itensSelecionaveis } from "../lib/colunas";
import { chaveDaComanda, rotuloEnvio } from "../lib/envios";
import { acaoDoCard, esticarLimiares, MODULE_CONFIG, NIVEL_CLASSES, nivelUrgencia, rotuloOrigem, tempoDesde } from "../lib/kds";

import { Button } from "@/components/ui/button";

/** Cartão de um pedido na coluna.
 *
 *  `mobile` deixa botão e timer no tamanho de dedo/cozinha; o índice do numpad
 *  só existe no desktop. `span` (1-3) é o cartão duplo/triplo: ocupa 2-3
 *  colunas da grade e distribui os itens em colunas internas, pra pedido
 *  grande caber na tela sem rolar.
 *
 *  Um pedido pode estar desenhado DUAS vezes: inteiro em Produção e, quando
 *  parte dele já saiu, um cartão `_parcial` em Prontos só com o que saiu. E a
 *  mesa que mandou mais de uma vez tem um cartão por envio ("2º envio").
 */
export default function PedidoCard({
  pedido, column, onAcao, products, kdsIndex, isSelected, mobile, span = 1, limiares, up,
  selecionados = [], onAlternarItem, onDesfazerItem, onLimparSelecao,
}) {
  const [advancing, setAdvancing] = useState(false);
  const [marcando, setMarcando] = useState(null);
  const items = pedido._itens || [];
  const modulo = pedido.modulo || "Cardapio";
  const modConfig = MODULE_CONFIG[modulo] || MODULE_CONFIG.Cardapio;
  const ModIcon = (pedido.tipo_entrega || "").toLowerCase() === "pickup" ? Store : modConfig.icon;

  const since = tempoDesde(pedido);
  // Produto de setor mais demorado estica a régua do cartão (tempo do setor)
  const regua = esticarLimiares(limiares, pedido._tempoMax);
  const nivel = NIVEL_CLASSES[nivelUrgencia(since, regua)];

  const findProductPhoto = (nome) => {
    if (!products || !nome) return null;
    const m = products.find((p) => (p.name || "").toLowerCase() === nome.toLowerCase());
    return m?.photo_url || m?.simple_photo_url || null;
  };

  const acao = acaoDoCard(pedido, column, up, selecionados.length);
  const handleAcao = async () => {
    setAdvancing(true);
    try { await onAcao(pedido, acao); } finally { setAdvancing(false); }
  };
  const handleDesfazer = async (item) => {
    setMarcando(item._idx);
    try { await onDesfazerItem(pedido, item); } finally { setMarcando(null); }
  };

  const ehChamar = acao.tipo === "chamar";
  const IconeAcao = acao.tipo === "entregar" ? HandPlatter
    : (column.status === "pronto" || acao.tipo === "marcar_selecionados" ? CheckCircle2 : ArrowRight);
  // Só a fila da cozinha seleciona; o subnúmero do teclado conta só os
  // selecionáveis (os que ainda não saíram).
  const selecionaveis = onAlternarItem ? itensSelecionaveis(pedido) : [];
  const naFila = column.status === "producao" && !pedido._parcial;
  const selecionando = acao.tipo === "marcar_selecionados";

  return (
    <div
      id={kdsIndex !== undefined ? `kds-card-${kdsIndex}` : undefined}
      className={`rounded-xl border-[3px] shadow-sm overflow-hidden flex flex-col transition-colors duration-300 ${isSelected ? "border-orange-500 z-10" : nivel.card} ${pedido._parcial ? "border-dashed" : ""}`}
      style={{ gridColumn: span > 1 ? `span ${span}` : undefined, ...(isSelected ? { animation: "kds-selected-pulse 1.5s ease-in-out infinite" } : {}) }}
    >
      <div className={`flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 ${isSelected ? "bg-orange-500/10" : nivel.header} border-b border-black/5 dark:border-white/10`}>
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-mono font-bold text-base ${nivel.texto}`}>{pedido.codigo || `#${(pedido.id || "").slice(0, 5)}`}</span>
            <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${modConfig.color}`}>
              <ModIcon className="w-3 h-3" />{rotuloOrigem(pedido)}
            </span>
            {rotuloEnvio(pedido) && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/80 text-white">
                {rotuloEnvio(pedido)}
              </span>
            )}
            {pedido._parcial && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/15 text-current">
                Parte da comanda
              </span>
            )}
          </div>
          {pedido.cliente_nome && <span className={`text-[11px] truncate max-w-[200px] ${nivel.suave}`}>{pedido.cliente_nome}</span>}
        </div>
        <Timer since={since} grande={mobile} escuro={nivel.timerEscuro} atrasoMin={regua?.vermelho} />
      </div>

      <div className="px-3 sm:px-4 py-2.5 flex-1" style={span > 1 ? { columnCount: span, columnGap: "1.25rem" } : undefined}>
        {items.map((item, idx) => (
          <ItemLinha
            key={item._idx ?? idx}
            item={item}
            nivel={nivel}
            subNumero={isSelected && selecionaveis.includes(item) ? selecionaveis.indexOf(item) + 1 : null}
            marcado={selecionados.includes(item._idx)}
            onTocar={naFila && onAlternarItem ? (it) => onAlternarItem(pedido, it) : null}
            onDesfazer={naFila && onDesfazerItem ? handleDesfazer : null}
            ocupado={marcando === item._idx}
            naFila={column.status === "producao"}
            desde={since}
            findProductPhoto={findProductPhoto}
            produtos={products}
          />
        ))}
      </div>

      {pedido._ocultos && (
        <div className={`px-3 sm:px-4 pb-2 flex items-center gap-1.5 text-[11px] font-semibold ${nivel.suave}`}>
          <EyeOff className="w-3.5 h-3.5 shrink-0" />
          +{pedido._ocultos.qtd} {pedido._ocultos.qtd === 1 ? "item" : "itens"} · {pedido._ocultos.setores.join(", ")}
        </div>
      )}
      {pedido.observacao && (
        <div className="px-3 sm:px-4 pb-2"><Observacao texto={pedido.observacao} rotulo="Pedido:" /></div>
      )}
      {acao.erro && (
        <div className="px-3 sm:px-4 pb-2">
          <div className="flex items-center gap-2 rounded-lg bg-white text-red-700 border border-red-300 px-2.5 py-1.5 text-xs font-semibold">
            <XCircle className="w-4 h-4 shrink-0" /><span className="truncate">UP Entrega: {acao.erro}</span>
          </div>
        </div>
      )}

      <div className="px-3 pb-3 flex items-center gap-2">
        {!mobile && kdsIndex !== undefined && (
          <div className={`min-w-[36px] h-11 flex items-center justify-center rounded-lg text-sm font-bold transition-all ${isSelected ? "bg-orange-500 text-white shadow-md scale-110" : nivel.indice}`}>
            {kdsIndex + 1}
          </div>
        )}
        {acao.tipo === "nenhuma" && (
          <div className={`flex-1 flex items-center justify-center gap-2 rounded-lg border border-dashed border-current/30 ${mobile ? "h-[52px] text-sm" : "h-11 text-xs"} font-semibold ${nivel.suave}`}>
            {pedido._soCancelados
              ? <XCircle className="w-4 h-4 text-red-600" />
              : pedido._parcial
              ? <CheckCircle2 className="w-4 h-4" />
              : ((pedido.tipo_entrega || "").toLowerCase() === "pickup" ? <Store className="w-4 h-4" /> : <Bike className="w-4 h-4" />)}
            {acao.nota}
          </div>
        )}
        {acao.tipo === "up_status" && (
          <div className={`flex-1 flex items-center justify-center gap-2 rounded-lg bg-blue-600 text-white ${mobile ? "h-[52px] text-base" : "h-11 text-sm"} font-bold px-3`}>
            <Bike className="w-5 h-5 shrink-0" />
            <span className="truncate">{acao.label}{acao.entregador ? ` · ${acao.entregador}` : ""}</span>
          </div>
        )}
        {acao.tipo === "confirmar_cancelamento" && (
          <Button disabled={advancing} onClick={handleAcao} data-e2e="kds-confirmar-cancelamento"
            className={`flex-1 min-w-0 gap-2 font-bold shadow-sm bg-red-600 hover:bg-red-700 text-white ${mobile ? "h-[52px] text-base" : "h-11 text-sm"}`}>
            <XCircle className="w-4 h-4 shrink-0" />
            <span className="truncate">{acao.label}</span>
          </Button>
        )}
        {(acao.tipo === "avancar" || acao.tipo === "chamar" || acao.tipo === "entregar" || selecionando) && (
          <Button disabled={advancing} onClick={handleAcao} title={acao.label}
            className={`flex-1 min-w-0 gap-2 font-bold shadow-sm ${mobile ? "h-[52px] text-base" : "h-11 text-sm"} ${
              ehChamar ? "bg-[#0ea5e9] hover:bg-[#0284c7] text-white"
                : selecionando ? "bg-orange-500 hover:bg-orange-600 text-white" : nivel.botao}`}>
            {advancing ? "..." : ehChamar
              ? <><img src="/logo-upentrega-v3.png" alt="UP" className="w-5 h-5 object-contain" /> Chamar Entregador</>
              : <>{!selecionando && <IconeAcao className="w-5 h-5 shrink-0" />}<span className="truncate">{acao.label}</span></>}
          </Button>
        )}
        {selecionando && onLimparSelecao && (
          <Button variant="outline" disabled={advancing} onClick={() => onLimparSelecao(chaveDaComanda(pedido))}
            title="Limpar a seleção desta comanda"
            className={`shrink-0 p-0 ${mobile ? "h-[52px] w-[52px]" : "h-11 w-11"} bg-white/70 text-zinc-700 border-zinc-300 hover:bg-white`}>
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>
    </div>
  );
}
